"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCostUsd, useUsageLedger } from "@/lib/usage/usage.hooks";

const compact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const parseUtc = (dateStr: string): number =>
  new Date(dateStr.includes("T") || dateStr.includes("Z") ? dateStr : `${dateStr.replace(" ", "T")}Z`).getTime();

const relativeTime = (dateStr: string) => {
  const diff = Date.now() - parseUtc(dateStr);
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export const DashboardRecentActivity = () => {
  const { data, isLoading } = useUsageLedger(8);
  const rows = data?.usage_ledger?.events ?? [];

  return (
    <Card className="mx-4 lg:mx-6">
      <CardHeader>
        <CardTitle>Recent activity</CardTitle>
        <CardDescription>Latest API usage across your keys.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col divide-y rounded-md border">
          {isLoading ? (
            <li className="px-4 py-8 text-center text-sm text-muted-foreground">Loading...</li>
          ) : rows.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-muted-foreground">No activity yet.</li>
          ) : (
            rows.map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 text-sm">
                <div className="min-w-0">
                  <span className="font-medium">{e.model}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · {compact.format(e.input_tokens + e.output_tokens)} tokens
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-3 tabular-nums">
                  <span>{formatCostUsd(e.cost_cents)}</span>
                  <span className="text-xs text-muted-foreground">{relativeTime(e.occurred_at)}</span>
                </div>
              </li>
            ))
          )}
        </ul>
      </CardContent>
    </Card>
  );
};
